import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useStripe } from '@stripe/react-stripe-js'
import { Form, json, Link, Navigate, useActionData, useNavigate, useSubmit } from 'react-router-dom'
import './review.css'

// COMPONENTS
import * as Icons from '@icons'
import { Column } from '@components/templates/column'
import Avatar from '@components/molecules/avatar'
import Button from '@components/atoms/button'
import Card from '@components/atoms/card'
import Divider from '@components/atoms/divider'
import GroupBanner from '@components/organisims/group-banner'
import GroupProgressBar from '@components/molecules/group-progress-bar'
import PaymentMethodBrand from '@components/atoms/payment-method-brand'
import SplitRecommendationCard from '@components/molecules/split-recommendation-card'
import ErrorModal from '@components/molecules/error-modal'

// TYPES
import type { Stripe } from '@stripe/stripe-js'
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { IoTClient } from '../../../../../_mqtt/config'
import { createLimitedAction } from '@utils/actions'
import { priceFormatter } from '@utils/number-helper'
import { isNonEmptyString, isObject } from '@utils/type-checkers'
import { createResponseObj, FailedResponse, isFailedResponse, StripeAPI, VirtualCardAPI } from '@apis'
import { useGroupLoaderData, usePledgeOutletContext } from '../..'
import { queryClient } from '@/lib/queryClient'


const formId = 'group-create-pledge-review'
let stripe: Stripe | null = null
const minSplitCount = 2
const maxSplitCount = 20
const getPeopleSplittingCountStorageKey = (groupId: string) => `group-${groupId}-people-splitting-count`
const getSuggestedContributionStorageKey = (groupId: string) => `group-${groupId}-suggested-contribution`

const GroupCreatePledgeReviewScreen: FC<{}> = () => {
    const { user: clerkUser } = useUser()
    const group = useGroupLoaderData()
    const [{ lockedSlots, pledge, setupIntent }, setState] = usePledgeOutletContext()
    const navigate = useNavigate()
    const submit = useSubmit()
    stripe = useStripe()

    const actionData = useActionData()
    const [showError, setError] = useState(false)
    const [error, setErrorData] = useState<FailedResponse>()
    const [highlightPledgeAmount, setHighlightPledgeAmount] = useState(false)
    const highlightTimeoutRef = useRef<number>()

    const feeAmount = 0
    const creditAmount = useMemo(() => Number(clerkUser?.publicMetadata?.creditAmount || 0), [clerkUser?.publicMetadata?.creditAmount])
    const pledgeAmount = pledge.amount || 0
    const initializedGroupIdRef = useRef<string>()
    const claimedPledges = useMemo(() => (group.pledges || []).filter((item) => !!item.transactionId || !!item.user?.id), [group.pledges])
    const isParticipantOrganizer = useMemo(() => {
        if (group.type !== 'merchant') return false

        const joinedParticipantCount = claimedPledges.filter((item) => {
            if (item.transactionId && !item.user?.id) return true
            return item.user?.id !== group.merchant?.user?.id
        }).length

        return joinedParticipantCount === 0
    }, [group.type, group.merchant?.user?.id, claimedPledges])
    const defaultPeopleSplittingCount = useMemo(() => {
        const participantSlots = (group.pledges || []).filter((item) => !item.user?.id || item.user.id !== group.merchant?.user?.id).length
        return Math.min(maxSplitCount, Math.max(minSplitCount, participantSlots || minSplitCount))
    }, [group.pledges, group.merchant?.user?.id])
    const [peopleSplittingCount, setPeopleSplittingCount] = useState(defaultPeopleSplittingCount)
    const recommendedContribution = useMemo(() => {
        const targetAmount = group.targetAmount || 0
        return (targetAmount > 0 && peopleSplittingCount > 0)
            ? Math.round(targetAmount / peopleSplittingCount)
            : 0
    }, [group.targetAmount, peopleSplittingCount])
    const appliedCreditAmount = useMemo(() => (
        pledge.useCredit ? Math.min(Math.max(0, creditAmount), Math.max(0, pledgeAmount)) : 0
    ), [pledge.useCredit, creditAmount, pledgeAmount])
    const user = useMemo((): User => ({
        id: clerkUser?.id,
        givenName: clerkUser?.firstName,
        familyName: clerkUser?.lastName,
        imageUrl: clerkUser?.imageUrl,
    }), [clerkUser?.id, clerkUser?.firstName, clerkUser?.lastName, clerkUser?.imageUrl])

    const requestBody = useMemo(() => JSON.stringify({
        ...pledge, user,
        transactionId: setupIntent?.id,
    } satisfies Pledge), [pledge, user])

    useEffect(() => {
        if (isFailedResponse(actionData)) {
            setErrorData(actionData)
            setError(true)
        }
    }, [actionData])

    useEffect(() => {
        return () => {
            if (highlightTimeoutRef.current) window.clearTimeout(highlightTimeoutRef.current)
        }
    }, [])

    useEffect(() => {
        if (initializedGroupIdRef.current === group.id) return
        initializedGroupIdRef.current = group.id

        const storedCount = Number(sessionStorage.getItem(getPeopleSplittingCountStorageKey(group.id)))
        const initialPeopleSplittingCount = Number.isFinite(storedCount)
            ? Math.min(maxSplitCount, Math.max(minSplitCount, Math.floor(storedCount)))
            : defaultPeopleSplittingCount

        setPeopleSplittingCount(initialPeopleSplittingCount)
    }, [group.id, defaultPeopleSplittingCount])

    useEffect(() => {
        if (!isParticipantOrganizer) return
        sessionStorage.setItem(getPeopleSplittingCountStorageKey(group.id), `${peopleSplittingCount}`)
    }, [isParticipantOrganizer, group.id, peopleSplittingCount])

    useEffect(() => {
        if (!isParticipantOrganizer) return
        sessionStorage.setItem(getSuggestedContributionStorageKey(group.id), `${Math.max(0, recommendedContribution)}`)
    }, [isParticipantOrganizer, group.id, recommendedContribution])

    const ref = useRef<HTMLDialogElement>(null)
    function openFeeInfo() { if (ref.current) ref.current.showModal() }
    function closeFeeInfo() { if (ref.current) ref.current.close() }

    function handleBack() {
        navigate(-1)
    }

    function increasePeopleSplittingCount() {
        setPeopleSplittingCount((current) => Math.min(maxSplitCount, current + 1))
    }

    function decreasePeopleSplittingCount() {
        setPeopleSplittingCount((current) => Math.max(minSplitCount, current - 1))
    }

    function applyRecommendedAmount() {
        if (recommendedContribution <= 0) return
        setState((currentState) => ({
            ...currentState,
            pledge: { ...currentState.pledge, amount: recommendedContribution },
        }))

        setHighlightPledgeAmount(false)
        window.requestAnimationFrame(() => {
            setHighlightPledgeAmount(true)
            if (highlightTimeoutRef.current) window.clearTimeout(highlightTimeoutRef.current)
            highlightTimeoutRef.current = window.setTimeout(() => setHighlightPledgeAmount(false), 1400)
        })
    }

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault()

        const form = document.getElementById(formId) as HTMLFormElement
        if (form) submit(form)

        const pledgeId = pledge.id
        if (!pledgeId) return

        const newState = { ...lockedSlots }
        newState[pledgeId] = { status: 'settling', user, updatedAt: new Date().getTime(), amount: pledge.amount || 0 }

        IoTClient.getInstance().publishMessage(`/group/${group.id}/locking/`, {
            Event: 'MODIFY',
            Type: 'locking',
            Item: newState
        } satisfies LockingEvent, { retain: true })
    }

    // Check if payment method is required
    const requiresPaymentMethod = useMemo(() => {
        if (pledgeAmount === 0) return false
        // If using credit and credit covers the full amount, no payment method needed
        if (pledge.useCredit && appliedCreditAmount >= pledgeAmount) return false
        return true
    }, [pledge.useCredit, pledgeAmount, appliedCreditAmount])

    if ((pledge.amount || 0) < 100) return <Navigate to="../../welcome" replace={true} />
    else if (!pledge.paymentMethod && requiresPaymentMethod) return <Navigate to="../pm" replace={true} />
    else return (
        <Form id={formId} onSubmit={handleSubmit} method="POST" className="flex flex-col gap-4">
            <input type="hidden" name="isMerchantGroupWithNoOrganizer" value={`${group.type === 'merchant' && !group.organizer?.id}`} />
            <input type="hidden" name="pledges" value={JSON.stringify(group.pledges)} />
            <Column.Header.Start>
                <a onClick={handleBack}>
                    <img src="/icons/arrow-left.svg" alt="back" style={{ width: '1.4rem', height: '1.4rem' }} draggable="false" />
                </a>
            </Column.Header.Start>
            <div className='group-banner-section' style={{ marginBottom: '1rem' }}>
                <GroupBanner group={group} isGroupMember={true} />
            </div>
            <input onChange={() => { }} name="requestBody" value={requestBody} hidden />
            <GroupProgressBar goal={group.targetAmount || 0 / 100} pledge={((group.collectedAmount || 0) / 100) + ((pledge?.amount || 0) / 100)} />
            {isParticipantOrganizer && (
                <SplitRecommendationCard
                    peopleSplittingCount={peopleSplittingCount}
                    recommendedContribution={recommendedContribution}
                    onDecreasePeopleCount={decreasePeopleSplittingCount}
                    onIncreasePeopleCount={increasePeopleSplittingCount}
                    onApplyRecommendedAmount={applyRecommendedAmount}
                />
            )}
            <h3 className='text-center'>Review Details</h3>
            <Card>
                <div className="flex flex-row items-center" style={{ gap: 'inherit' }}>
                    <Avatar user={user} style={{ height: '3.125rem', width: '3.125rem' }} />
                    <div className="flex flex-col items-stretch flex-1">
                        <p className="text-body-2 text-gray-500 text-left">Your contribution</p>
                        <div className="flex flex-row items-center justify-between" style={{ gap: 'inherit' }}>
                            <h2 className={highlightPledgeAmount ? 'pledge-review-amount-highlight' : undefined}>{priceFormatter.format(pledge.amount / 100)}</h2>
                            <p className="text-body-2 text-blue-500"><Link to="../../contribute">Edit</Link></p>
                        </div>
                    </div>
                </div>
                <div className='flex flex-row items-center gap-4'>
                    <p className="text-body-2" style={{ color: 'var(--splitcard-gray-500)' }}><strong>{priceFormatter.format(feeAmount)}</strong> is your estimated fee.</p>
                    <Icons.Info onClick={openFeeInfo} type='filled' height="1rem" width="1rem" style={{ color: 'var(--splitcard-blue-500)', cursor: 'pointer' }} />
                    <dialog ref={ref}>
                        <div>
                            <div className="flex flex-col items-stretch" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'left', margin: '1rem', background: 'var(--splitcard-white)', gap: '1rem', padding: '1.3125rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                                <h2>Splitcard Fee</h2>
                                <p><strong>Good news — we’ve waived our fee!</strong> Splitcard is free to use for a limited time.</p>
                                <p>Now’s the perfect time to try it and <strong>share with friends and family</strong></p>
                                <div className="flex flex-col items-stretch gap-2.5">
                                    <Button theme="primary" formMethod="dialog" onClick={closeFeeInfo}>Got it!</Button>
                                </div>
                            </div>
                        </div>
                    </dialog>
                </div>
                {(() => {
                    const usedCreditAmount = appliedCreditAmount
                    const isFullyFunded = pledgeAmount > 0 && usedCreditAmount >= pledgeAmount
                    const paymentMethod = pledge.paymentMethod
                    const hasPaymentMethod = !!paymentMethod

                    if (usedCreditAmount === 0 && !hasPaymentMethod) return null

                    return (
                        <>
                            <Divider />
                            <div className="flex flex-row items-center justify-between">
                                <p className="text-body"><strong>Your payment Info</strong></p>
                                <p className="text-body-2 text-blue-500"><Link to="../pm">Edit</Link></p>
                            </div>

                            {usedCreditAmount > 0 && (
                                <div className='flex flex-row justify-between'>
                                    <div className="flex flex-row items-center gap-4" style={{ marginBottom: (!isFullyFunded && hasPaymentMethod) ? '0.75rem' : 0 }}>
                                        <PaymentMethodBrand
                                            brand="splitcard-credit"
                                            height='2rem' width='2rem' style={{ height: '2rem' }}
                                        />
                                        <div className="flex flex-col items-stretch flex-1">
                                            <p className='text-body text-green-600'><strong>Splitcard Credit</strong></p>
                                        </div>
                                    </div>
                                    <p className='text-body text-green-600'><strong className={highlightPledgeAmount ? 'pledge-review-amount-highlight' : undefined}>{priceFormatter.format(usedCreditAmount / 100)}</strong></p>
                                </div>
                            )}

                            {(!isFullyFunded && paymentMethod) && (
                                <div className='flex flex-row justify-between'>
                                    <div className="flex flex-row items-center gap-4">
                                        <PaymentMethodBrand
                                            brand={paymentMethod.type === 'credit_card'
                                                ? paymentMethod.cardType || paymentMethod.type
                                                : paymentMethod.type
                                            }
                                            height='2rem' width='2rem' style={{ height: '2rem' }}
                                        />
                                        <div className="flex flex-col items-stretch flex-1">
                                            <p className='text-body'><strong>{
                                                paymentMethod.type === 'credit_card'
                                                    ? `Card ending in ${paymentMethod.lastFour}`
                                                    : paymentMethod.type.replace('_', ' ')
                                            }</strong></p>
                                        </div>
                                    </div>
                                    <p className='text-body'><strong className={highlightPledgeAmount ? 'pledge-review-amount-highlight' : undefined}>{priceFormatter.format((pledgeAmount - usedCreditAmount) / 100)}</strong></p>
                                </div>
                            )}
                        </>
                    )
                })()}
            </Card>
            {pledge.paymentMethod && (() => {
                const chargeAmount = Math.max(0, pledgeAmount - appliedCreditAmount)

                return (
                    <p className="text-body-2 text-gray-500 text-center">The maximum amount that you can be charged is <strong className={highlightPledgeAmount ? 'pledge-review-amount-highlight' : undefined}>{priceFormatter.format(chargeAmount / 100)}</strong>.</p>
                )
            })()}
            <Column.Footer>
                <Button form={formId} type="submit">Join Group</Button>
            </Column.Footer>
            <ErrorModal
                state={[showError, setError]}
                error={error}
                title="Issue Joining Group"
                fallbackMessage="There was an issue adding you to the group. If this continues please contact support."
            />
        </Form >
    )
}

const action = createLimitedAction({
    limit: 'joinGroups',
    getRedirectPath: ({ params }) => `/group/${params.groupId}`,
}, async ({ request, params }) => {
    try {
        const formData = await request.formData()
        const groupId = params.groupId
        const requestBody = JSON.parse(`${formData.get("requestBody")}`)
        const isMerchantGroupWithNoOrganizer = formData.get('isMerchantGroupWithNoOrganizer') === 'true'
        const pledges = JSON.parse(`${formData.get("pledges")}`)

        console.log("Pledge Data:", requestBody)
        if (!isNonEmptyString(groupId) || !isPledge(requestBody)) throw createResponseObj({
            status: 'failed', type: 'local', name: 'missing-resources',
            message: `Missing required resources. ${JSON.stringify(requestBody)}`
        })


        // Generate a setupIntent if one wasn't provided and payment method exists
        var transactionId = requestBody.transactionId
        const useCredit = requestBody.useCredit
        const paymentMethod = requestBody.paymentMethod

        // If credit covers the full amount and no payment method, send empty string as transactionId
        if (useCredit && !paymentMethod && !isNonEmptyString(transactionId)) {
            transactionId = ''
        } else if (!isNonEmptyString(transactionId) && paymentMethod) {
            const { clientSecret } = await StripeAPI.CreateSetupIntentRequest.fetch()
            const { setupIntent, error } = await stripe?.confirmCardSetup(clientSecret, {
                payment_method: paymentMethod.token
            }) || {}

            if (!setupIntent || error) throw error || new Error("Failed to generate Setup intent")
            transactionId = setupIntent.id
        }

        if (isMerchantGroupWithNoOrganizer) await VirtualCardAPI.UpdatePledgeRequest.fetch(groupId, { ...requestBody, id: pledges[0].id, transactionId })
        else if (!!requestBody.id) await VirtualCardAPI.UpdatePledgeRequest.fetch(groupId, { ...requestBody, id: requestBody.id, transactionId })
        else await VirtualCardAPI.AddPledgeRequest.fetch(groupId, { ...requestBody, transactionId })

        queryClient.invalidateQueries({ queryKey: ['user-groups'] })

        sessionStorage.setItem('showNotificationPrompt', 'true')
        return json('success', { status: 200 })
    } catch (error) {
        console.error(error)
        return error
    }
})

// ToDo: Pledge Validation Schema
const isPledge = (value: unknown): value is Pledge => (
    isObject(value)
)

const PledgeReviewRoute: RouteObject = {
    path: 'review',
    element: <GroupCreatePledgeReviewScreen />,
    action,
}

export default PledgeReviewRoute
